import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '@/lib/api';
import { PageHeading, LoadingState, ErrorNotice } from '@/components/portal/PortalPage';
import { BriefcaseIcon } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { Select } from '@/components/ui/Select';
import { DatePicker } from '@/components/ui/DatePicker';
import { Badge } from '@/components/ui/Badge';

type Source = {
  id: string;
  periodMonth: string;
  mission: { id: string; title: string };
  company: { id: string; name: string };
  currency: string;
  costDailyRate: string | null;
  calculation: {
    workedDays: string;
    unpricedDays: string;
    amountExclTax: string | null;
    rateOrigin: 'ACCEPTED_QUOTE' | 'MISSION';
    overtimeHours: string;
    lines: { days: string; costDailyRate: string; amountExclTax: string }[];
  };
};
type Invoice = {
  id: string;
  reference: string;
  status: { code: string; label: string };
  company: string;
  amountInclTax: string;
  currency: string;
  paidAt: string | null;
  periodMonth: string;
  mission: { title: string | null } | null;
};
export const InvoicesPage = () => {
  const query = useQuery({
    queryKey: ['portal-invoices'],
    queryFn: () => httpClient.get<Invoice[]>('/me/invoices'),
  });
  const [error, setError] = useState('');
  const openFile = async (id: string) => {
    try {
      const file = await httpClient.get<{ url: string }>('/me/invoices/' + id + '/file');
      window.open(file.url, '_blank', 'noopener,noreferrer');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Fichier indisponible.');
    }
  };
  return (
    <div className="portal-page">
      <PageHeading
        title="Mes factures"
        description="Déposez votre facture après validation du CRA et suivez son traitement jusqu’au paiement."
        icon={<BriefcaseIcon />}
      />
      <div className="flex justify-end">
        <Link to="/factures/nouvelle">
          <Button>Déposer une facture</Button>
        </Link>
      </div>
      {error && <ErrorNotice>{error}</ErrorNotice>}
      {query.isPending ? (
        <LoadingState>Chargement…</LoadingState>
      ) : query.isError ? (
        <ErrorNotice>{query.error.message}</ErrorNotice>
      ) : (
        <div className="space-y-3">
          {query.data.map((invoice) => (
            <article
              key={invoice.id}
              className="border-navy-100 flex flex-wrap items-center gap-4 rounded-[10px] border bg-white p-5"
            >
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold text-navy-900">{invoice.reference}</h2>
                <p className="text-ink-500 mt-1 text-sm">
                  {invoice.company} · {invoice.mission?.title ?? 'Mission'} · {invoice.periodMonth}
                </p>
                {invoice.paidAt && (
                  <p className="mt-1 text-xs text-ink-500">
                    Paiement enregistré le {new Date(invoice.paidAt).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
              <strong>
                {new Intl.NumberFormat('fr-FR', {
                  style: 'currency',
                  currency: invoice.currency,
                }).format(Number(invoice.amountInclTax))}
              </strong>
              <Badge>{invoice.status.label}</Badge>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void openFile(invoice.id);
                }}
              >
                Voir le PDF
              </Button>
            </article>
          ))}
          {!query.data.length && (
            <p className="text-ink-500 rounded-[10px] border border-navy-100 bg-white p-6">
              Aucune facture déposée.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
export const InvoiceCreatePage = () => {
  const query = useQuery({
    queryKey: ['portal-invoice-sources'],
    queryFn: () => httpClient.get<Source[]>('/me/invoices/sources'),
  });
  const navigate = useNavigate();
  const cache = useQueryClient();
  const [missionId, setMissionId] = useState('');
  const [timesheetId, setTimesheetId] = useState('');
  const [reference, setReference] = useState('');
  const [amount, setAmount] = useState('');
  const [tax, setTax] = useState('20');
  const [dueDate, setDueDate] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const sources = query.data ?? [];
  const missions = [
    ...new Map(sources.map((source) => [source.mission.id, source.mission])).values(),
  ];
  const selectedMissionId = missions.some((mission) => mission.id === missionId)
    ? missionId
    : missions.length === 1
      ? missions[0]!.id
      : '';
  const missionSources = sources.filter((source) => source.mission.id === selectedMissionId);
  const selected = missionSources.find((source) => source.id === timesheetId);
  const formatMonth = (month: string) =>
    new Intl.DateTimeFormat('fr-FR', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(month + '-01T12:00:00Z'));
  const money = (value: string) =>
    new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: selected?.currency ?? 'EUR',
    }).format(Number(value));
  const quantity = (value: string) =>
    new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(Number(value));
  const calculation = selected?.calculation;
  const amountDiffers =
    calculation?.amountExclTax != null &&
    amount.trim() !== '' &&
    Number.isFinite(Number(amount.replace(',', '.'))) &&
    Math.abs(Number(amount.replace(',', '.')) - Number(calculation.amountExclTax)) >= 0.005;
  const submit = async () => {
    if (!file || !selected) return;
    setSaving(true);
    setError('');
    try {
      const form = new FormData();
      form.set('timesheetId', timesheetId);
      form.set('reference', reference);
      form.set('amountExclTax', amount);
      form.set('taxRate', tax);
      if (dueDate) form.set('dueDate', dueDate);
      form.set('file', file);
      await httpClient.post('/me/invoices', form);
      await cache.invalidateQueries({ queryKey: ['portal-invoices'] });
      await cache.invalidateQueries({ queryKey: ['portal-invoice-sources'] });
      void navigate('/factures', { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Dépôt impossible.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="portal-page">
      <Link to="/factures" className="text-sm text-ink-500">
        ← Mes factures
      </Link>
      <PageHeading
        title="Déposer une facture"
        description="La facture doit être établie au nom de votre entreprise de rattachement."
        icon={<BriefcaseIcon />}
      />
      {query.isPending ? (
        <LoadingState>Chargement des CRA validés…</LoadingState>
      ) : query.isError ? (
        <ErrorNotice>{query.error.message}</ErrorNotice>
      ) : !sources.length ? (
        <div className="rounded-[10px] border border-navy-100 bg-white p-6">
          <h2 className="font-semibold">Aucun CRA disponible à facturer</h2>
          <p className="text-ink-500 mt-2 text-sm">
            Un CRA validé, une entreprise de facturation et l’autorisation de dépôt sur votre
            mission sont nécessaires. Les périodes déjà facturées ne sont pas proposées. Contactez
            votre responsable si nécessaire.
          </p>
        </div>
      ) : (
        <form
          className="space-y-5 rounded-[10px] border border-navy-100 bg-white p-6"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <div className="grid gap-5 md:grid-cols-2">
            <Select
              label="Mission *"
              value={selectedMissionId}
              placeholder="Choisir une mission"
              required
              options={missions.map((mission) => ({ value: mission.id, label: mission.title }))}
              onChange={(event) => {
                setMissionId(event.target.value);
                setTimesheetId('');
                setAmount('');
              }}
            />
            <Select
              label="CRA validé *"
              value={selected?.id ?? ''}
              required
              disabled={!selectedMissionId || missionSources.length === 0}
              placeholder={
                !selectedMissionId ? 'Choisissez d’abord une mission' : 'Choisir le mois à facturer'
              }
              hint="Seuls les CRA validés et non encore facturés sont proposés."
              options={missionSources.map((source) => ({
                value: source.id,
                label: formatMonth(source.periodMonth),
              }))}
              onChange={(event) => {
                setTimesheetId(event.target.value);
                setAmount(
                  missionSources.find((source) => source.id === event.target.value)?.calculation
                    ?.amountExclTax ?? '',
                );
              }}
            />
            {selected && (
              <section
                aria-label="Calcul du montant HT"
                className="rounded-lg border border-navy-100 bg-navy-50 p-4 md:col-span-2"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="font-semibold text-navy-900">Calcul du montant HT</h2>
                  <span className="text-sm text-ink-500">{formatMonth(selected.periodMonth)}</span>
                </div>
                <p className="mt-1 text-sm text-ink-500">
                  {calculation?.rateOrigin === 'ACCEPTED_QUOTE'
                    ? 'CJM issu du devis accepté, selon les conditions applicables aux journées travaillées.'
                    : 'CJM enregistré sur la mission. Aucun CJM issu d’un devis accepté n’a été retrouvé.'}
                </p>
                <dl className="mt-4 space-y-2">
                  {calculation?.lines.map((line) => (
                    <div
                      key={line.costDailyRate}
                      className="flex flex-wrap items-center justify-between gap-2 text-sm"
                    >
                      <dt>
                        {quantity(line.days)} jours × {money(line.costDailyRate)} / jour
                      </dt>
                      <dd className="font-medium text-navy-900">{money(line.amountExclTax)}</dd>
                    </div>
                  ))}
                  <div className="flex flex-wrap items-center justify-between gap-2 border-t border-navy-200 pt-3">
                    <dt className="text-sm font-medium">Montant HT calculé</dt>
                    <dd className="text-lg font-semibold text-violet-700">
                      {calculation?.amountExclTax != null
                        ? money(calculation.amountExclTax)
                        : 'À compléter'}
                    </dd>
                  </div>
                </dl>
                {calculation && Number(calculation.unpricedDays) > 0 && (
                  <p className="mt-3 text-sm text-ink-500">
                    CJM manquant pour {quantity(calculation.unpricedDays)} jours : demandez à votre
                    responsable de compléter les conditions financières.
                  </p>
                )}
                {calculation && Number(calculation.overtimeHours) > 0 && (
                  <p className="mt-3 text-sm text-ink-500">
                    {quantity(calculation.overtimeHours)} heures supplémentaires ne sont pas
                    incluses dans ce calcul journalier. Ajustez le montant selon les conditions
                    convenues.
                  </p>
                )}
                {amountDiffers && (
                  <p className="mt-3 text-sm text-ink-500">
                    Le montant saisi diffère du calcul. Vérifiez qu’il correspond au total HT de
                    votre facture PDF.
                  </p>
                )}
              </section>
            )}
            <TextField
              label="Référence de votre facture *"
              value={reference}
              required
              maxLength={120}
              onChange={(event) => setReference(event.target.value)}
            />
            <TextField
              label={'Montant HT' + (selected ? ' (' + selected.currency + ')' : '') + ' *'}
              value={amount}
              hint="Prérempli selon le CRA et le CJM. Ajustez-le si nécessaire pour correspondre au PDF."
              required
              inputMode="decimal"
              onChange={(event) => setAmount(event.target.value)}
            />
            <TextField
              label="TVA (%) *"
              value={tax}
              required
              inputMode="decimal"
              onChange={(event) => setTax(event.target.value)}
            />
            <DatePicker label="Échéance" value={dueDate} onValueChange={setDueDate} />
          </div>
          {selected && (
            <p className="rounded-lg bg-navy-50 p-4 text-sm">
              Émetteur : <strong>{selected.company.name}</strong>. Période :{' '}
              {formatMonth(selected.periodMonth)}.
            </p>
          )}
          <label className="block space-y-2 text-sm font-medium">
            Facture PDF *
            <input
              type="file"
              accept="application/pdf,.pdf"
              required
              className="block w-full rounded-lg border border-navy-200 p-3"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </label>
          {error && <ErrorNotice>{error}</ErrorNotice>}
          <footer className="flex justify-end gap-3">
            <Button
              variant="secondary"
              disabled={saving}
              onClick={() => {
                void navigate('/factures');
              }}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              loading={saving}
              disabled={!selected || !file || !reference || !amount}
            >
              Déposer la facture
            </Button>
          </footer>
        </form>
      )}
    </div>
  );
};
